import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { ProductCard } from "../components/site/ProductCard";
import { useRfq } from "../components/site/RfqContext";
import { api, fileUrl, useCategories } from "../lib/api";
import { Seo, crumbsLd } from "../components/site/Seo";

const clip = (s, n = 158) => (s.length > n ? `${s.slice(0, n - 1).replace(/\s+\S*$/, "")}…` : s);

export default function Category() {
  const { slug } = useParams();
  const { open } = useRfq();
  const { categories } = useCategories();
  const { data, isLoading, isError } = useQuery({ queryKey: ["category", slug], queryFn: () => api.get(`/categories/${slug}`).then((r) => r.data) });
  const cat = data?.category;
  const products = Array.isArray(data?.products) ? data.products : [];

  if (isError) return <NotFoundBlock />;
  if (isLoading || !cat) return <div className="min-h-screen bg-[#07090e]" data-testid="category-loading" />;

  return (
    <div data-testid="category-page">
      <Seo
        path={`/products/${cat.slug}`}
        title={`${cat.name} Manufacturer in India | Autocrat Rubber`}
        description={clip(`${cat.description} Request a quote from Autocrat Rubber Industries, Gurugram.`)}
        image={cat.image}
        jsonLd={crumbsLd([["Home", "/"], ["Products", "/products"], [cat.name, `/products/${cat.slug}`]])}
      />
      <PageHero testId="category-hero" eyebrow={cat.tagline} image={fileUrl(cat.image)} lines={cat.name.split(" ").length > 1 ? [cat.name.split(" ").slice(0, -1).join(" "), { text: cat.name.split(" ").slice(-1)[0], className: "text-outline" }] : [{ text: cat.name, className: "text-white" }]}
        crumbs={[{ to: "/", label: "Home" }, { to: "/products", label: "Products" }, { label: cat.name }]}>
        <p>{cat.description}</p>
        <button data-testid="category-rfq-button" onClick={() => open(cat.name)} className="btn-electric mt-8">Quote for {cat.name} <ArrowRight className="h-4 w-4" /></button>
      </PageHero>
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="container-x">
          <p className="font-mono-x text-xs uppercase tracking-widest text-slate-500">{products.length} parts in this family</p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} catSlug={cat.slug} />)}
          </div>
          {products.length === 0 && <p className="text-sm text-slate-500">Parts for this family are being added. Send an RFQ with your drawing.</p>}
          <div className="mt-20 border-t border-slate-200 pt-10">
            <p className="eyebrow text-slate-500">Other families</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {categories.filter((c) => c.slug !== slug).map((c) => (
                <Link key={c.id} to={`/products/${c.slug}`} data-testid={`other-category-${c.slug}`} className="border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 transition-colors hover:border-[#0a6cff] hover:text-[#0a6cff]">{c.name}</Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export const NotFoundBlock = () => (
  <section data-testid="not-found" className="flex min-h-screen flex-col items-start justify-center bg-[#07090e] px-6 text-white sm:px-16">
    <Seo title="Page not found | Autocrat Rubber Industries" description="This page does not exist. Browse the Autocrat Rubber Industries product catalogue." noindex />
    <p className="eyebrow text-sky-400">404</p>
    <h1 className="font-display mt-4 text-6xl font-black uppercase sm:text-8xl">Part not found.</h1>
    <Link to="/products" className="btn-electric mt-10">Browse catalogue</Link>
  </section>
);
